import { useState } from 'react';
import { t } from '../lib/i18n';

/**
 * The small month in the sidebar: a way to get to a date, and nothing else.
 *
 * It navigates on its own — flipping through months here must not drag the main view along, because
 * looking for "that Tuesday in June" while keeping this week on screen is exactly what it is for.
 * Picking a day is what moves the view.
 */
export function MiniCalendar({
  month,
  selected,
  onPick,
}: {
  /** The month to open on. Changing it outside re-syncs the mini calendar to follow the view. */
  month: Date;
  selected: Date;
  onPick: (day: Date) => void;
}): React.JSX.Element {
  const [shown, setShown] = useState(() => startOfMonth(month));

  // Follows the main view when that jumps somewhere else, without fighting it while browsing.
  const [lastMonth, setLastMonth] = useState(() => month.getTime());

  if (month.getTime() !== lastMonth) {
    setLastMonth(month.getTime());
    setShown(startOfMonth(month));
  }

  return (
    <div className="minical">
      <header className="minical__head">
        <button
          type="button"
          className="button button--quiet button--compact"
          aria-label={t('calendar.previous')}
          onClick={() => setShown(new Date(shown.getFullYear(), shown.getMonth() - 1, 1))}
        >
          ‹
        </button>

        <span className="small strong">
          {`${t(`calendar.month.${shown.getMonth()}`)} ${shown.getFullYear()}`}
        </span>

        <button
          type="button"
          className="button button--quiet button--compact"
          aria-label={t('calendar.next')}
          onClick={() => setShown(new Date(shown.getFullYear(), shown.getMonth() + 1, 1))}
        >
          ›
        </button>
      </header>

      <div className="minical__grid">
        {[0, 1, 2, 3, 4, 5, 6].map((weekday) => (
          <span key={weekday} className="minical__weekday">
            {t(`calendar.weekday.${weekday}`).slice(0, 1)}
          </span>
        ))}

        {daysOf(shown).map((day) => (
          <button
            key={day.toISOString()}
            type="button"
            className={`minical__day${day.getMonth() === shown.getMonth() ? '' : ' is-outside'}${
              sameDay(day, selected) ? ' is-selected' : ''
            }${sameDay(day, new Date()) ? ' is-today' : ''}`}
            onClick={() => onPick(day)}
          >
            {day.getDate()}
          </button>
        ))}
      </div>
    </div>
  );
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function daysOf(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1 - month.getDay());

  return Array.from(
    { length: 42 },
    (_, index) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + index),
  );
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
